import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientsAPI, invitationsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Search, Building2, Pencil, Trash2, Loader2,
  Mail, Send, RefreshCw, X, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

const statusConfig = {
  pending: { label: 'En attente', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  accepted: { label: 'Acceptee', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Annulee', icon: XCircle, className: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom_entreprise: '', adresse: '', telephone: '', email_contact: '', personne_contact: '',
  });

  // Invite state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', client_id: '', message: '' });
  const [showInvites, setShowInvites] = useState(false);
  const [invitesLoading, setInvitesLoading] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try { const r = await clientsAPI.getAll(); setClients(r.data); }
    catch { toast.error('Erreur lors du chargement des clients'); }
    finally { setLoading(false); }
  };

  const fetchInvitations = async () => {
    setInvitesLoading(true);
    try { const r = await invitationsAPI.list(); setInvitations(r.data); }
    catch { /* ignore */ }
    finally { setInvitesLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast.success('Client mis a jour');
      } else {
        await clientsAPI.create(formData);
        toast.success('Client cree');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client?')) return;
    try { await clientsAPI.delete(id); toast.success('Client supprime'); fetchClients(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      nom_entreprise: client.nom_entreprise,
      adresse: client.adresse || '',
      telephone: client.telephone || '',
      email_contact: client.email_contact || '',
      personne_contact: client.personne_contact || '',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ nom_entreprise: '', adresse: '', telephone: '', email_contact: '', personne_contact: '' });
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) { toast.error('Courriel requis'); return; }
    setInviteLoading(true);
    try {
      await invitationsAPI.send({
        email: inviteForm.email,
        client_id: inviteForm.client_id || null,
        message: inviteForm.message || null,
      });
      toast.success(`Invitation envoyee a ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ email: '', client_id: '', message: '' });
      if (showInvites) fetchInvitations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setInviteLoading(false); }
  };

  const handleResendInvite = async (id) => {
    try { await invitationsAPI.resend(id); toast.success('Invitation renvoyee'); }
    catch { toast.error('Erreur'); }
  };

  const handleCancelInvite = async (id) => {
    try { await invitationsAPI.cancel(id); toast.success('Invitation annulee'); fetchInvitations(); }
    catch { toast.error('Erreur'); }
  };

  const filteredClients = clients.filter((c) =>
    c.nom_entreprise.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.personne_contact?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-600">Gerez vos clients et leurs informations.</p>
        </div>
        <div className="flex gap-2">
          {/* Invite Button */}
          <Button variant="outline" onClick={() => setInviteOpen(true)} data-testid="invite-client-btn">
            <Mail className="w-4 h-4 mr-2" />
            Inviter
          </Button>
          {/* New Client Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingClient ? 'Modifier le client' : 'Nouveau client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise *</Label>
                  <Input value={formData.nom_entreprise} onChange={e => setFormData({...formData, nom_entreprise: e.target.value})} required data-testid="client-nom-entreprise" />
                </div>
                <div className="space-y-2">
                  <Label>Personne contact</Label>
                  <Input value={formData.personne_contact} onChange={e => setFormData({...formData, personne_contact: e.target.value})} data-testid="client-personne-contact" />
                </div>
                <div className="space-y-2">
                  <Label>Courriel</Label>
                  <Input type="email" value={formData.email_contact} onChange={e => setFormData({...formData, email_contact: e.target.value})} data-testid="client-email" />
                </div>
                <div className="space-y-2">
                  <Label>Telephone</Label>
                  <Input value={formData.telephone} onChange={e => setFormData({...formData, telephone: e.target.value})} data-testid="client-telephone" />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input value={formData.adresse} onChange={e => setFormData({...formData, adresse: e.target.value})} data-testid="client-adresse" />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={formLoading} data-testid="client-submit">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : editingClient ? 'Mettre a jour' : 'Creer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Toggle Invites */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher un client..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="client-search" />
            </div>
            <Button
              variant={showInvites ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setShowInvites(!showInvites); if (!showInvites) fetchInvitations(); }}
              data-testid="toggle-invitations-btn"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Invitations
              {invitations.filter(i => i.status === 'pending').length > 0 && (
                <span className="ml-1.5 bg-amber-400 text-[#1D1D1B] rounded-full w-5 h-5 text-xs flex items-center justify-center font-bold">
                  {invitations.filter(i => i.status === 'pending').length}
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invitations Panel */}
      {showInvites && (
        <Card data-testid="invitations-panel">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-medium text-slate-900">Invitations envoyees</h3>
              <Button variant="ghost" size="sm" onClick={fetchInvitations} disabled={invitesLoading}>
                <RefreshCw className={`w-4 h-4 ${invitesLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {invitesLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F9B438]" /></div>
            ) : invitations.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">Aucune invitation envoyee</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Courriel</TableHead>
                    <TableHead className="hidden md:table-cell">Client</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map(inv => {
                    const st = statusConfig[inv.status] || statusConfig.pending;
                    const Icon = st.icon;
                    return (
                      <TableRow key={inv.id} data-testid={`invite-row-${inv.id}`}>
                        <TableCell className="font-medium">{inv.email}</TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">{inv.client_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`gap-1 ${st.className}`}>
                            <Icon className="w-3 h-3" /> {st.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-500">{formatDate(inv.created_at)}</TableCell>
                        <TableCell>
                          {inv.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleResendInvite(inv.id)} title="Renvoyer" data-testid={`resend-invite-${inv.id}`}>
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleCancelInvite(inv.id)} title="Annuler" data-testid={`cancel-invite-${inv.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          {filteredClients.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">{searchQuery ? 'Aucun client trouve.' : 'Aucun client enregistre.'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead className="hidden md:table-cell">Contact</TableHead>
                  <TableHead className="hidden lg:table-cell">Courriel</TableHead>
                  <TableHead className="hidden lg:table-cell">Telephone</TableHead>
                  <TableHead className="hidden md:table-cell">Cree le</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map(client => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <Link to={`/portail/clients/${client.id}`} className="font-medium text-primary hover:underline">
                        {client.nom_entreprise}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{client.personne_contact || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">{client.email_contact || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">{client.telephone || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{formatDate(client.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(client)} data-testid={`edit-client-${client.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(client.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-client-${client.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite Modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setInviteOpen(false)} />
          <Card className="relative w-full max-w-md z-10" data-testid="invite-modal">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F9B438]/10 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#F9B438]" />
                  </div>
                  <h3 className="font-heading font-semibold text-[#1D1D1B]">Inviter un client</h3>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setInviteOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-5 space-y-4">
                <div className="space-y-2">
                  <Label>Courriel du client *</Label>
                  <Input
                    type="email"
                    placeholder="client@entreprise.com"
                    value={inviteForm.email}
                    onChange={e => setInviteForm({...inviteForm, email: e.target.value})}
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Associer a un client existant</Label>
                  <Select value={inviteForm.client_id} onValueChange={v => setInviteForm({...inviteForm, client_id: v === 'none' ? '' : v})}>
                    <SelectTrigger data-testid="invite-client-select">
                      <SelectValue placeholder="Aucun (optionnel)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nom_entreprise}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Message personnel (optionnel)</Label>
                  <Textarea
                    placeholder="Bienvenue sur notre portail..."
                    value={inviteForm.message}
                    onChange={e => setInviteForm({...inviteForm, message: e.target.value})}
                    rows={3}
                    data-testid="invite-message-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setInviteOpen(false)}>Annuler</Button>
                  <Button className="flex-1" onClick={handleSendInvite} disabled={inviteLoading} data-testid="send-invite-btn">
                    {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Envoyer l'invitation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
